import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchNodeDefinitions } from 'api/node_definitions/endpoints/searchNodeDefinitions';
import type { NodeDefinitionRow } from 'api/node_definitions/endpoints/getNodeDefinitions';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

// The minimal identifying shape this control deals in — the same identifying projection the search
// endpoint validates, so a search result passes straight through. Aliased rather than redeclared
// because the two are the same type by identity, not by coincidence.
export type NodeDefinitionReference = NodeDefinitionRow;

interface Props {
  // The selected node definition, or null for "none". A whole reference rather than a bare id, since
  // a caller may need the name and zone as well as the key.
  readonly value: NodeDefinitionReference | null;
  readonly onChange: (value: NodeDefinitionReference | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict the searchable set to one zone (server-side `zone_name` equals). Undefined widens to
  // all authorized node definitions.
  readonly zoneName?: string;
  // Restrict to one node type (server-side equals). Undefined widens to every type. Not a union:
  // node types are server-driven (`GET /node_types`).
  readonly type?: string;
}

const getKey = (n: NodeDefinitionReference) => n.node_definition_id;

const getLabel = (n: NodeDefinitionReference): string => n.name;

/**
 * Searchable dropdown for node definitions keyed on `node_definition_id`, for callers that store the numeric id. The
 * id↔display-name expansion is internal: the caller works in plain ids, and `resolve` hydrates a stored id to its name.
 * Selection returns the id.
 *
 * Sibling of the path-keyed {@link EditNodeDefinitionPathReference}, whose props are kept in step with these: only `value` /
 * `onChange` differ, in type *and* in meaning, which is the whole reason there are two components rather than one with a
 * `string | number` value.
 *
 * Backed by server-side search (`GET /node_definitions?search=…`), so the option list is not bounded by what fits in one
 * eager fetch. Two-column row layout (name + zone, muted right-aligned as informational context); the server matches on
 * name only, zone is shown so users can disambiguate visually.
 */
export const EditNodeDefinitionReference = ({ value, onChange, label, placeholder, mandatory, disabled, zoneName, type }: Props) => {
  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<NodeDefinitionReference>>(
    () => ({
      kind: 'search',
      search: (query, limit) => searchNodeDefinitions({ search: query || undefined, limit, zone_name: zoneName, type }),
      // Resolve a saved id back to its label by id alone — independent of the zone/type scope, so an
      // existing selection still renders even once the scope no longer admits it.
      resolve: async (key) => {
        const { items } = await searchNodeDefinitions({ node_definition_id: Number(key), limit: 1 });
        return items[0] ?? null;
      },
      // Shown while `resolve` is in flight so a key-only value reads as selected rather than blank.
      interimValue: (key) => ({ node_definition_id: Number(key), name: `Node #${key}`, zone_name: '' }),
    }),
    [zoneName, type]
  );

  return (
    <EditReference<NodeDefinitionReference>
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(n, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{n.name}</Highlighter>
          </span>
          <span className="text-body-secondary">{n.zone_name}</span>
        </div>
      )}
    />
  );
};
